import {trace, context, SpanKind, SpanStatusCode} from '@opentelemetry/api'
import config from '../config'

// Create a tracer for error handling
const tracer = trace.getTracer('error-tracer')

interface ErrorDetails {
  message: string
  stack?: string
  componentName?: string
  source?: string
  requestUrl?: string
  requestMethod?: string
  statusCode?: number
  responseData?: any
  additionalInfo: Record<string, any> // Changed to required to avoid undefined checks
}

/**
 * Logs errors to console and telemetry system
 */
export class ErrorLogger {
  /**
   * Log a network error with details
   */
  static logNetworkError(error: any, source: string = 'unknown') {
    try {
      if (error?.message) {
        const details: ErrorDetails = {
          message: error?.message,
          stack: error?.stack,
          source,
          additionalInfo: {
            timestamp: new Date().toISOString(),
            environment: config.ENVIRONMENT,
          },
        }

        // Handle axios errors
        if (error?.config) {
          details.requestUrl = error.config.url
          details.requestMethod = error.config.method
        }

        // Handle response data if available
        if (error?.response) {
          details.statusCode = error.response.status
          details.additionalInfo.statusText = error.response.statusText
          // Only capture a safe subset of response data to avoid sensitive info leakage
          try {
            if (typeof error.response.data === 'object') {
              details.responseData = {
                message: error.response.data?.message,
                code: error.response.data?.code,
                error: error.response.data?.error,
              }
            } else {
              details.responseData = {rawResponse: String(error.response.data).substring(0, 200)}
            }
          } catch (e) {
            details.responseData = {error: 'Could not parse response data'}
            console.warn('Error parsing response data:', e)
          }
        }
        // Send to telemetry
        this.sendToTelemetry(details, 'network.error')
      }
    } catch (e) {}
  }

  /**
   * Log a JavaScript error with details
   */
  static logJsError(error: Error, componentName: string = 'unknown') {
    try {
      if (error?.message) {
        const details: ErrorDetails = {
          message: error?.message,
          stack: error?.stack,
          componentName,
          source: 'javascript',
          additionalInfo: {
            timestamp: new Date().toISOString(),
          },
        }
        this.sendToTelemetry(details, 'js.error')
      }
    } catch (e) {}
  }

  /**
   * Log a GraphQL error with details
   */
  static logGraphQLError(error: any) {
    try {
      if (error?.message) {
        const details: ErrorDetails = {
          message: error?.message,
          stack: error?.stack,
          source: 'graphql',
          additionalInfo: {
            timestamp: new Date().toISOString(),
            environment: config.ENVIRONMENT,
            graphqlPath: error?.path,
            graphqlLocations: error?.locations,
            operationName: error?.operation?.operationName,
          },
        }
        this.sendToTelemetry(details, 'graphql.error')
      }
    } catch (e) {}
  }

  /**
   * Send error details to telemetry system
   */
  private static sendToTelemetry(details: ErrorDetails, errorType: string) {
    try {
      tracer.startActiveSpan(
        `error.${errorType}`,
        {
          kind: SpanKind.INTERNAL,
          attributes: {
            'error.type': errorType,
            'error.message': details.message,
            'error.stack': details.stack,
            'error.component': details.componentName,
            'error.source': details.source,
            'http.url': details.requestUrl,
            'http.method': details.requestMethod,
            'http.status_code': details.statusCode,
            'error.timestamp': details.additionalInfo.timestamp,
            'app.environment': details.additionalInfo.environment,
          },
        },
        context.active(),
        (span) => {
          // Add any additional attributes
          if (details.additionalInfo) {
            Object.entries(details.additionalInfo).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                span.setAttribute(`error.context.${key}`, String(value))
              }
            })
          }

          // Set status to error
          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: details.message ?? 'Unknown error',
          })

          span.end()
        }
      )
    } catch (e) {
      // Ensure telemetry failures don't affect the application
      console.warn('Failed to send error to telemetry:', e)
    }
  }
}

/**
 * Set up global error handlers
 */
export function setupGlobalErrorHandlers() {
  try {
    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason instanceof Error) {
        ErrorLogger.logJsError(event.reason, 'unhandledRejection')
      }
    })
  } catch (e) {
    console.warn('Failed to set up error listeners:', e)
  }
}
